import { fireEvent, render } from '@testing-library/preact';
import { expect } from 'chai';
import { VNode } from 'preact';

import {
  CurrentItemVariant,
  ItemActionDetail,
  Metadata,
  SelectionMode
} from '../../../UNSAFE_Collection';
import { RootEnvironmentProvider } from '../../../UNSAFE_Environment';
import type { RootEnvironment } from '../../../UNSAFE_Environment';
import { Gridlines } from '../../../UNSAFE_ListView';
import { Keys } from '../../../utils/UNSAFE_keys';
import { matchTranslationBundle } from '../../../utils/UNSAFE_matchTranslationBundle';
import { CollectionGestureContext } from '../../../hooks/PRIVATE_useCollectionGestureContext';

import { populateData } from './listViewTestFixtures';
import type { Employee } from './listViewTestFixtures';
import { getContents, getItems, getRoot } from './testHelpers';

export type Props<K, D> = {
  currentItemVariant?: CurrentItemVariant;
  data: { data: Employee<K>; metadata: Metadata<K> }[];
  gridlines?: Gridlines;
  initialKey?: K;
  onItemAction?: (detail: ItemActionDetail<K, D>) => void;
  selectedKeys?: Keys<K>;
  selectionMode?: SelectionMode;
  onClick?: () => void;
};

type TestComponentProps<K extends string | number, D> = (props: Props<K, D>) => VNode;

export const testRender = (TestComponent: TestComponentProps<any, any>) => {
  it('initial render', async function () {
    const data = populateData(100) as Employee<string>[];
    const dataMetadata: { data: Employee<string>; metadata: Metadata<string> }[] = data.map(
      (data) => {
        return { data: data, metadata: { key: data.id } };
      }
    );
    const list = render(<TestComponent data={dataMetadata}></TestComponent>);
    const items = await getItems(list);
    expect(items.length).eq(25);
  });

  // skip this test in jest, only run it in karma mocha
  if (typeof jest == typeof undefined) {
    it('initial render with sparkle', async () => {
      const data = populateData(100) as Employee<string>[];
      let dataMetadata: { data: Employee<string>; metadata: Metadata<string> }[] = data.map(
        (data) => {
          return { data: data, metadata: { key: data.id } };
        }
      );

      // add suggestions
      dataMetadata[3].metadata.suggestion = { enable: true };
      dataMetadata[7].metadata.suggestion = { enable: true };
      dataMetadata[9].metadata.suggestion = { enable: true };

      const suggestions = dataMetadata.filter((d) => d.metadata.suggestion);
      dataMetadata = dataMetadata.filter((d) => !d.metadata.suggestion);
      dataMetadata.unshift(...suggestions);

      const list = render(<TestComponent data={dataMetadata}></TestComponent>);
      const grid = await getRoot(list);
      const sparkle = grid.querySelector('.oj-collection-sparkle')
        ?.firstElementChild as HTMLElement;
      expect(sparkle !== undefined).eq(true);

      const items = await getItems(list);
      expect(items?.length).eq(25);
      expect(items[0].dataset['ojSuggestion']).eq('true');
      expect(items[1].dataset['ojSuggestion']).eq('true');
      expect(items[2].dataset['ojSuggestion']).eq('true');

      const sparkleHeight = items[0].offsetHeight + items[1].offsetHeight + items[2].offsetHeight;
      expect(sparkleHeight).eq(sparkle.offsetHeight);
    });
  }
};

export const testAria = (TestComponent: TestComponentProps<any, any>) => {
  it('aria structure and properties (select-none)', async function () {
    const data = populateData(10) as Employee<string>[];
    const dataMetadata: { data: Employee<string>; metadata: Metadata<string> }[] = data.map(
      (data) => {
        return { data: data, metadata: { key: data.id } };
      }
    );
    const list = render(<TestComponent data={dataMetadata}></TestComponent>);
    const grid = await getRoot(list);
    expect(grid.getAttribute('aria-rowcount')).eq('10');
    expect(grid.getAttribute('aria-multiselectable')).eq(null);

    const items = await getItems(list);
    expect(items.length).eq(10);
    expect(items[0].getAttribute('aria-rowindex')).eq('1');

    const cells = await getContents(list);
    expect(cells.length).eq(10);
    expect(cells[0].getAttribute('aria-colindex')).eq('1');
    expect(cells[0].getAttribute('aria-selected')).eq(null);
  });

  it('aria structure and properties (single-select)', async function () {
    const data = populateData(10) as Employee<string>[];
    const dataMetadata: { data: Employee<string>; metadata: Metadata<string> }[] = data.map(
      (data) => {
        return { data: data, metadata: { key: data.id } };
      }
    );
    const list = render(<TestComponent data={dataMetadata} selectionMode="single"></TestComponent>);
    const grid = await getRoot(list);
    expect(grid.getAttribute('aria-rowcount')).eq('10');
    expect(grid.getAttribute('aria-multiselectable')).eq('false');

    const items = await getItems(list);
    expect(items.length).eq(10);
    expect(items[0].getAttribute('aria-rowindex')).eq('1');

    const cells = await getContents(list);
    expect(cells.length).eq(10);
    expect(cells[0].getAttribute('aria-colindex')).eq('1');
    expect(cells[0].getAttribute('aria-selected')).eq('false');
  });

  it('aria structure and properties (multi-select)', async function () {
    const data = populateData(10) as Employee<string>[];
    const dataMetadata: { data: Employee<string>; metadata: Metadata<string> }[] = data.map(
      (data) => {
        return { data: data, metadata: { key: data.id } };
      }
    );

    const locale = matchTranslationBundle(['en'], new Set(['en-US', 'en']));
    const { default: translations } = await import(`../../../resources/nls/${locale}/bundle.ts`);
    const env: Partial<RootEnvironment> = {
      translations: { '@oracle/oraclejet-preact': translations }
    };
    const list = render(
      <RootEnvironmentProvider environment={env}>
        <TestComponent data={dataMetadata} selectionMode="multiple"></TestComponent>
      </RootEnvironmentProvider>
    );

    const grid = await getRoot(list);
    expect(grid.getAttribute('aria-rowcount')).eq('10');
    expect(grid.getAttribute('aria-multiselectable')).eq('true');

    const items = await getItems(list);
    expect(items.length).eq(10);
    expect(items[0].getAttribute('aria-rowindex')).eq('1');

    const cells = await getContents(list);
    expect(cells.length).eq(10);
    expect(cells[0].getAttribute('aria-colindex')).eq('1');
    expect(cells[0].getAttribute('aria-selected')).eq('false');
  });
};

export const testKeys = (TestComponent: TestComponentProps<any, any>) => {
  it('supports currentKey logic with numeric key', async () => {
    const data = populateData(100, true) as Employee<string>[];
    const dataMetadata: { data: Employee<string>; metadata: Metadata<string> }[] = data.map(
      (data) => {
        return { data: data, metadata: { key: data.id } };
      }
    );
    const list = render(<TestComponent data={dataMetadata}></TestComponent>);
    const grid = await getRoot(list);
    expect(grid.hasAttribute('aria-activedescendant')).eq(false);
    const items = await getContents(list);
    fireEvent.click(items[0]);
    expect(grid.getAttribute('aria-activedescendant')).eq(items[0].id);
    fireEvent.keyDown(grid, { key: 'ArrowDown' });
    expect(grid.getAttribute('aria-activedescendant')).eq(items[1].id);
    fireEvent.keyDown(grid, { key: 'ArrowUp' });
    expect(grid.getAttribute('aria-activedescendant')).eq(items[0].id);
  });

  it('supports currentKey logic with string key', async () => {
    const data = populateData(100) as Employee<string>[];
    const dataMetadata: { data: Employee<string>; metadata: Metadata<string> }[] = data.map(
      (data) => {
        return { data: data, metadata: { key: data.id } };
      }
    );
    const list = render(<TestComponent data={dataMetadata}></TestComponent>);
    const grid = await getRoot(list);
    expect(grid.hasAttribute('aria-activedescendant')).eq(false);
    const items = await getContents(list);
    fireEvent.click(items[0]);
    expect(grid.getAttribute('aria-activedescendant')).eq(items[0].id);
    fireEvent.keyDown(grid, { key: 'ArrowDown' });
    expect(grid.getAttribute('aria-activedescendant')).eq(items[1].id);
    fireEvent.keyDown(grid, { key: 'ArrowUp' });
    expect(grid.getAttribute('aria-activedescendant')).eq(items[0].id);
  });

  it('checking key type - string key', async () => {
    const data = populateData(3) as Employee<string>[];
    const dataMetadata: { data: Employee<string>; metadata: Metadata<string> }[] = data.map(
      (data) => {
        return { data: data, metadata: { key: data.id } };
      }
    );
    const list = render(<TestComponent data={dataMetadata}></TestComponent>);
    const items = await getItems(list);
    expect((items[0] as HTMLElement).dataset['ojKeyType'] === undefined).eq(true);
    expect((items[1] as HTMLElement).dataset['ojKeyType'] === undefined).eq(true);
    expect((items[2] as HTMLElement).dataset['ojKeyType'] === undefined).eq(true);
  });

  it('checking key type - numeric key', async () => {
    const data = populateData(3, true) as Employee<number>[];
    const dataMetadata: { data: Employee<number>; metadata: Metadata<number> }[] = data.map(
      (data) => {
        return { data: data, metadata: { key: data.id } };
      }
    );
    const list = render(<TestComponent data={dataMetadata}></TestComponent>);
    const items = await getItems(list);
    expect((items[0] as HTMLElement).dataset['ojKeyType']).eq('number');
    expect((items[1] as HTMLElement).dataset['ojKeyType']).eq('number');
    expect((items[2] as HTMLElement).dataset['ojKeyType']).eq('number');
  });
};

export const testGridlines = (TestComponent: TestComponentProps<any, any>) => {
  const isTopGridlineVisible = (item: HTMLElement) => {
    const className = item.className;
    return className.indexOf('gridlineTop_visible') > 0;
  };

  const isBottomGridlineVisible = (item: HTMLElement) => {
    const className = item.className;
    return className.indexOf('gridlineBottom_visible') > 0;
  };

  const isGridlineHidden = (item: HTMLElement) => {
    return !isTopGridlineVisible(item) && !isBottomGridlineVisible(item);
  };

  it('gridlines - gridlines are hidden', async () => {
    const data = populateData(3) as Employee<string>[];
    const dataMetadata: { data: Employee<string>; metadata: Metadata<string> }[] = data.map(
      (data) => {
        return { data: data, metadata: { key: data.id } };
      }
    );
    const list = render(<TestComponent data={dataMetadata}></TestComponent>);
    const items = await getItems(list);
    expect(isGridlineHidden(items[0])).to.be.true;
    expect(isGridlineHidden(items[1])).to.be.true;
    expect(isGridlineHidden(items[2])).to.be.true;
  });

  it('gridlines - bottom gridlines are visible', async () => {
    const data = populateData(3) as Employee<string>[];
    const dataMetadata: { data: Employee<string>; metadata: Metadata<string> }[] = data.map(
      (data) => {
        return { data: data, metadata: { key: data.id } };
      }
    );
    const visible = { item: 'visible' as const, bottom: 'visible' as const };
    const list = render(<TestComponent data={dataMetadata} gridlines={visible}></TestComponent>);
    const items = await getItems(list);
    expect(isBottomGridlineVisible(items[0])).to.be.true;
    expect(isBottomGridlineVisible(items[1])).to.be.true;
    expect(isBottomGridlineVisible(items[2])).to.be.true;
  });

  it('gridlines - bottom gridlines are visible except for the last list item', async () => {
    const data = populateData(3) as Employee<string>[];
    const dataMetadata: { data: Employee<string>; metadata: Metadata<string> }[] = data.map(
      (data) => {
        return { data: data, metadata: { key: data.id } };
      }
    );
    const visibleExceptLast = { item: 'visible' as const };
    const list = render(
      <TestComponent data={dataMetadata} gridlines={visibleExceptLast}></TestComponent>
    );
    const items = await getItems(list);
    expect(isBottomGridlineVisible(items[0])).to.be.true;
    expect(isBottomGridlineVisible(items[1])).to.be.true;
    expect(isBottomGridlineVisible(items[2])).to.be.false;
  });

  it('gridlines - bottom gridlines are visible; The top gridlines of the first list item is visible', async () => {
    const data = populateData(3) as Employee<string>[];
    const dataMetadata: { data: Employee<string>; metadata: Metadata<string> }[] = data.map(
      (data) => {
        return { data: data, metadata: { key: data.id } };
      }
    );
    const visibleWithFirst = {
      item: 'visible' as const,
      top: 'visible' as const,
      bottom: 'visible' as const
    };
    const list = render(
      <TestComponent data={dataMetadata} gridlines={visibleWithFirst}></TestComponent>
    );
    const items = await getItems(list);
    expect(isTopGridlineVisible(items[0])).to.be.true;
    expect(isBottomGridlineVisible(items[0])).to.be.true;
    expect(isBottomGridlineVisible(items[1])).to.be.true;
    expect(isBottomGridlineVisible(items[2])).to.be.true;
  });

  it('gridlines - bottom gridlines are visible except for the last list item. The top gridlines of the first list item is visible.', async () => {
    const data = populateData(3) as Employee<string>[];
    const dataMetadata: { data: Employee<string>; metadata: Metadata<string> }[] = data.map(
      (data) => {
        return { data: data, metadata: { key: data.id } };
      }
    );
    const visibleWithFirstButNotLast = { item: 'visible' as const, top: 'visible' as const };
    const list = render(
      <TestComponent data={dataMetadata} gridlines={visibleWithFirstButNotLast}></TestComponent>
    );
    const items = await getItems(list);
    expect(isTopGridlineVisible(items[0])).to.be.true;
    expect(isBottomGridlineVisible(items[0])).to.be.true;
    expect(isBottomGridlineVisible(items[1])).to.be.true;
    expect(isBottomGridlineVisible(items[2])).to.be.false;
  });
};

export const testItemAction = (TestComponent: TestComponentProps<any, any>) => {
  const isMetadataEqual = (metadata1: Metadata<any>, metadata2: Metadata<any>) => {
    return metadata1.key === metadata2.key;
  };

  const isFocusHighlight = (item: HTMLElement) => {
    const className = item.className;
    return (
      className.indexOf('focusHighlight_isFocusHighlight') > 0 ||
      className.indexOf('MultiVariantStyles_compound') > 0
    );
  };

  it('ItemAction - test click on item', async () => {
    const data = populateData(100) as Employee<string>[];
    const dataMetadata: { data: Employee<string>; metadata: Metadata<string> }[] = data.map(
      (data) => {
        return { data: data, metadata: { key: data.id } };
      }
    );
    let count = 0;
    const onItemAction = <K, D>(detail: ItemActionDetail<K, D>) => {
      if (count === 0) {
        expect(detail.context.data, 'Context data is correct').eq(dataMetadata[1].data);
        expect(
          isMetadataEqual(detail.context.metadata, dataMetadata[1].metadata),
          'Context metadata is correct'
        ).eq(true);
      } else if (count === 1) {
        expect(detail.context.data, 'Context data is correct').eq(dataMetadata[1].data);
        expect(
          isMetadataEqual(detail.context.metadata, dataMetadata[1].metadata),
          'Context metadata is correct'
        ).eq(true);
      } else if (count === 2) {
        expect(detail.context.data, 'Context data is correct').eq(dataMetadata[2].data);
        expect(
          isMetadataEqual(detail.context.metadata, dataMetadata[2].metadata),
          'Context metadata is correct'
        ).eq(true);
      }
      count++;
    };
    const list = render(
      <TestComponent data={dataMetadata} onItemAction={onItemAction}></TestComponent>
    );
    const items = await getItems(list, 'row');
    fireEvent.click(items[1]);
    fireEvent.click(items[1]);
    fireEvent.click(items[2]);
    expect(count).eq(3);
  });

  it('ItemAction - test press enter on item', async () => {
    const data = populateData(100) as Employee<string>[];
    const dataMetadata: { data: Employee<string>; metadata: Metadata<string> }[] = data.map(
      (data) => {
        return { data: data, metadata: { key: data.id } };
      }
    );
    let count = 0;
    const onItemAction = <K, D>(detail: ItemActionDetail<K, D>) => {
      if (count === 1) {
        expect(detail.context.data, 'Context data is correct').eq(dataMetadata[1].data);
        expect(
          isMetadataEqual(detail.context.metadata, dataMetadata[1].metadata),
          'Context metadata is correct'
        ).eq(true);
      } else if (count === 2) {
        expect(detail.context.data, 'Context data is correct').eq(dataMetadata[1].data);
        expect(
          isMetadataEqual(detail.context.metadata, dataMetadata[1].metadata),
          'Context metadata is correct'
        ).eq(true);
      } else if (count === 3) {
        expect(detail.context.data, 'Context data is correct').eq(dataMetadata[2].data);
        expect(
          isMetadataEqual(detail.context.metadata, dataMetadata[2].metadata),
          'Context metadata is correct'
        ).eq(true);
      }
      count++;
    };
    const list = render(
      <TestComponent data={dataMetadata} onItemAction={onItemAction}></TestComponent>
    );
    const grid = await getRoot(list);
    const items = await getItems(list, 'row');
    fireEvent.click(items[0]);
    fireEvent.keyDown(grid, { key: 'ArrowDown' });
    fireEvent.keyDown(items[1], { key: 'Enter' });
    fireEvent.keyDown(items[1], { key: 'Enter' });
    fireEvent.keyDown(grid, { key: 'ArrowDown' });
    fireEvent.keyDown(items[2], { key: 'Enter' });
    expect(count).eq(4);
  });

  it('ItemAction - test press spacebar on item (Context value is embedded)', async () => {
    const data = populateData(100) as Employee<string>[];
    const dataMetadata: { data: Employee<string>; metadata: Metadata<string> }[] = data.map(
      (data) => {
        return { data: data, metadata: { key: data.id } };
      }
    );
    let count = 0;
    const onItemAction = <K, D>(detail: ItemActionDetail<K, D>) => {
      if (count === 1) {
        expect(detail.context.data, 'Context data is correct').eq(dataMetadata[1].data);
        expect(
          isMetadataEqual(detail.context.metadata, dataMetadata[1].metadata),
          'Context metadata is correct'
        ).eq(true);
      } else if (count === 2) {
        expect(detail.context.data, 'Context data is correct').eq(dataMetadata[1].data);
        expect(
          isMetadataEqual(detail.context.metadata, dataMetadata[1].metadata),
          'Context metadata is correct'
        ).eq(true);
      } else if (count === 3) {
        expect(detail.context.data, 'Context data is correct').eq(dataMetadata[2].data);
        expect(
          isMetadataEqual(detail.context.metadata, dataMetadata[2].metadata),
          'Context metadata is correct'
        ).eq(true);
      }
      count++;
    };
    const list = render(
      <CollectionGestureContext.Provider value={'embedded'}>
        <TestComponent data={dataMetadata} onItemAction={onItemAction}></TestComponent>
      </CollectionGestureContext.Provider>
    );
    const grid = await getRoot(list);
    const items = await getItems(list, 'row');
    fireEvent.click(items[0]);
    fireEvent.keyDown(grid, { key: 'ArrowDown' });
    fireEvent.keyDown(items[1], { key: ' ' });
    fireEvent.keyDown(items[1], { key: ' ' });
    fireEvent.keyDown(grid, { key: 'ArrowDown' });
    fireEvent.keyDown(items[2], { key: ' ' });
    expect(count).eq(4);
  });

  it('ItemAction - test press spacebar on item (Context value is default)', async () => {
    const data = populateData(100) as Employee<string>[];
    const dataMetadata: { data: Employee<string>; metadata: Metadata<string> }[] = data.map(
      (data) => {
        return { data: data, metadata: { key: data.id } };
      }
    );
    let count = 0;
    const onItemAction = <K, D>(detail: ItemActionDetail<K, D>) => {
      if (count === 1) {
        expect(detail.context.data, 'Context data is correct').eq(dataMetadata[1].data);
        expect(detail.context.metadata, 'Context metadata is correct').eq(dataMetadata[1].metadata);
      } else if (count === 2) {
        expect(detail.context.data, 'Context data is correct').eq(dataMetadata[1].data);
        expect(detail.context.metadata, 'Context metadata is correct').eq(dataMetadata[1].metadata);
      } else if (count === 3) {
        expect(detail.context.data, 'Context data is correct').eq(dataMetadata[2].data);
        expect(detail.context.metadata, 'Context metadata is correct').eq(dataMetadata[2].metadata);
      }
      count++;
    };
    const list = render(
      <CollectionGestureContext.Provider value={'default'}>
        <TestComponent data={dataMetadata} onItemAction={onItemAction}></TestComponent>
      </CollectionGestureContext.Provider>
    );
    const grid = await getRoot(list);
    const items = await getItems(list, 'row');
    fireEvent.click(items[0]);
    fireEvent.keyDown(grid, { key: 'ArrowDown' });
    fireEvent.keyDown(items[1], { key: ' ' });
    fireEvent.keyDown(items[1], { key: ' ' });
    expect(count).eq(1);
  });

  it('currentItemVariant - highlight', async () => {
    const data = populateData(3) as Employee<string>[];
    const dataMetadata: {
      data: Employee<string>;
      metadata: Metadata<string>;
    }[] = data.map((data) => {
      return { data: data, metadata: { key: data.id } };
    });
    const list = render(
      <TestComponent
        data={dataMetadata}
        initialKey={dataMetadata[1].metadata.key}
        currentItemVariant="highlight"></TestComponent>
    );
    const items = await getItems(list);
    expect(isFocusHighlight(items[0])).to.be.false;

    fireEvent.click(items[0]);
    expect(isFocusHighlight(items[0])).to.be.true;
  });

  it('currentItemVariant - none', async () => {
    const data = populateData(3) as Employee<string>[];
    const dataMetadata: {
      data: Employee<string>;
      metadata: Metadata<string>;
    }[] = data.map((data) => {
      return { data: data, metadata: { key: data.id } };
    });
    const list = render(
      <TestComponent
        data={dataMetadata}
        initialKey={dataMetadata[1].metadata.key}
        currentItemVariant="none"></TestComponent>
    );
    const items = await getItems(list);
    expect(isFocusHighlight(items[0])).to.be.false;

    fireEvent.click(items[0]);
    expect(isFocusHighlight(items[0])).to.be.false;
  });
};

export const testNavigation = (TestComponent: TestComponentProps<any, any>) => {
  it('arrow keys', async () => {
    const data = populateData(3) as Employee<string>[];
    const dataMetadata: { data: Employee<string>; metadata: Metadata<string> }[] = data.map(
      (data) => {
        return { data: data, metadata: { key: data.id } };
      }
    );
    const list = render(<TestComponent data={dataMetadata}></TestComponent>);
    const grid = await getRoot(list);
    expect(grid.hasAttribute('aria-activedescendant')).eq(false);
    const items = await getContents(list);
    fireEvent.click(items[0]);
    expect(grid.getAttribute('aria-activedescendant')).eq(items[0].id);
    fireEvent.keyDown(grid, { key: 'ArrowDown' });
    expect(grid.getAttribute('aria-activedescendant')).eq(items[1].id);
    fireEvent.keyDown(grid, { key: 'ArrowDown' });
    expect(grid.getAttribute('aria-activedescendant')).eq(items[2].id);
    fireEvent.keyDown(grid, { key: 'ArrowDown' });
    expect(grid.getAttribute('aria-activedescendant')).eq(items[2].id);
    fireEvent.keyDown(grid, { key: 'ArrowUp' });
    expect(grid.getAttribute('aria-activedescendant')).eq(items[1].id);
    fireEvent.keyDown(grid, { key: 'ArrowUp' });
    expect(grid.getAttribute('aria-activedescendant')).eq(items[0].id);
    fireEvent.keyDown(grid, { key: 'ArrowUp' });
    expect(grid.getAttribute('aria-activedescendant')).eq(items[0].id);
  });
};
